import { useState, useEffect } from "react";
import { db, auth } from "../firebase";
import { collection, getDocs, doc, setDoc, deleteDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { motion, AnimatePresence } from "framer-motion";
import { Search, PhoneCall, MapPin, Car, ChevronDown, Share2, Plus, X, Edit3, Trash2, LogIn } from "lucide-react";
import { Helmet } from "react-helmet-async";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "../components/ConfirmModal"; 

export default function Drivers() {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [search, setSearch] = useState("");
  const [vehicleFilter, setVehicleFilter] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoaded, setAuthLoaded] = useState(false);

  // Modal & Form States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    vehicleType: "",
    phone: "",
    place: "",
  });

  // Confirm Modal States
  const [confirmConfig, setConfirmConfig] = useState({
    isOpen: false,
    action: "", // "save" | "delete"
    message: "",
    docId: null
  });

  // Check Login Status
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setAuthLoaded(true);
    });
    return () => unsubscribe();
  }, []);

  const fetchData = async () => {
    const res = await getDocs(collection(db, "drivers"));
    setDrivers(res.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
  };

  useEffect(() => {
    fetchData();
  }, []);

  // നിലവിലെ യൂസറുടെ ഡാറ്റ കണ്ടെത്തുന്നു
  const myProfile = currentUser ? drivers.find(d => d.userId === currentUser.uid) : null;

  const filtered = drivers.filter((d) => {
    const place = d.place || "";
    const vehicle = d.vehicleType || "";
    return (
      (!vehicleFilter || vehicle === vehicleFilter) &&
      (d.name.toLowerCase().includes(search.toLowerCase()) ||
        place.toLowerCase().includes(search.toLowerCase()) ||
        vehicle.toLowerCase().includes(search.toLowerCase()))
    );
  });

  // സ്വന്തം പ്രൊഫൈൽ ഏറ്റവും മുകളിൽ വരാൻ Sort ചെയ്യുന്നു
  const sortedFiltered = [...filtered].sort((a, b) => {
    if (currentUser && a.userId === currentUser.uid) return -1;
    if (currentUser && b.userId === currentUser.uid) return 1;
    return 0;
  });

  // 🔥 ഷെയർ ചെയ്യാനുള്ള ഫംഗ്ഷൻ 🔥
  const handleShareAll = async () => {
    const shareText = "INFO PAYANGADI: യാത്രകൾക്കും ആവശ്യങ്ങൾക്കും വാഹനങ്ങൾ വിളിക്കാൻ ഈ ലിങ്ക് സന്ദർശിക്കുക.";
    const shareUrl = window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({
          title: "INFO PAYANGADI - Drivers Directory",
          text: shareText,
          url: shareUrl,
        });
      } catch (error) {
        console.log("ഷെയർ ചെയ്യാൻ കഴിഞ്ഞില്ല:", error);
      }
    } else {
      try {
        await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
        toast.success("ലിങ്ക് കോപ്പി ചെയ്തു! ഇനി ഷെയർ ചെയ്യാം.");
      } catch (err) {
        console.error("ലിങ്ക് കോപ്പി ചെയ്യാൻ പറ്റിയില്ല", err);
      }
    }
  };

  // 🔥 Open Modal (ലോഗിൻ ചെയ്തില്ലെങ്കിൽ Auth ലേക്ക് വിടും) 🔥
  const handleOpenRegister = () => {
    if (!authLoaded) return; 
    
    if (!currentUser) {
      toast.error("രജിസ്റ്റർ ചെയ്യാൻ ദയവായി ലോഗിൻ ചെയ്യുക!");
      navigate("/auth");
      return;
    }
    
    if (myProfile) {
      setForm({
        name: myProfile.name,
        place: myProfile.place,
        vehicleType: myProfile.vehicleType,
        phone: myProfile.phone,
      });
    } else {
      setForm({ name: "", place: "", vehicleType: "", phone: "" });
    }
    
    setIsModalOpen(true);
  };

  // കൃത്യമായ വാലിഡേഷനുകൾ
  const handlePreSubmit = () => {
    if (!currentUser) {
      toast.error("നിങ്ങൾ ലോഗിൻ ചെയ്തിട്ടില്ല! ദയവായി ലോഗിൻ ചെയ്യുക.");
      setIsModalOpen(false);
      navigate("/auth");
      return;
    }

    if (!form.name.trim()) return toast.error("ദയവായി ഡ്രൈവറുടെ പേര് നൽകുക.");
    if (!form.vehicleType) return toast.error("ദയവായി വാഹനത്തിന്റെ തരം തിരഞ്ഞെടുക്കുക.");
    if (!form.place.trim()) return toast.error("ദയവായി സ്ഥലം (സ്റ്റാൻഡ്) നൽകുക.");
    if (form.phone.length !== 10) return toast.error("ശരിയായ 10 അക്ക ഫോൺ നമ്പർ നൽകുക.");

    setConfirmConfig({
      isOpen: true,
      action: "save",
      message: myProfile ? "നിങ്ങളുടെ വിവരങ്ങൾ അപ്ഡേറ്റ് ചെയ്യണോ?" : "ഡ്രൈവറായി രജിസ്റ്റർ ചെയ്യണോ?"
    });
  };

  const handleDeleteClick = (id) => {
    if (!currentUser) return; 
    setConfirmConfig({
      isOpen: true,
      action: "delete",
      message: "നിങ്ങളുടെ പ്രൊഫൈൽ പൂർണ്ണമായും നീക്കം ചെയ്യണോ? ഈ പ്രവൃത്തി തിരിച്ചെടുക്കാനാകില്ല.",
      docId: id
    });
  };

  // Confirm Modal ൽ Yes അടിച്ചാൽ നടക്കുന്ന കാര്യങ്ങൾ
  const executeConfirmAction = async () => {
    setConfirmConfig({ ...confirmConfig, isOpen: false }); 
    
    if (!currentUser) {
      toast.error("Unauthorized action! Please log in.");
      navigate("/auth");
      return;
    }

    setLoading(true);

    try {
      if (confirmConfig.action === "delete") {
        await deleteDoc(doc(db, "drivers", confirmConfig.docId));
        toast.success("പ്രൊഫൈൽ വിജയകരമായി നീക്കം ചെയ്തു 🗑️");
        setIsModalOpen(false);
      } 
      else if (confirmConfig.action === "save") {
        const docIdToSave = myProfile ? myProfile.id : currentUser.uid;
        
        await setDoc(doc(db, "drivers", docIdToSave), {
          ...form,
          userId: currentUser.uid,
        });

        toast.success(myProfile ? "വിവരങ്ങൾ അപ്ഡേറ്റ് ചെയ്തു! 🎉" : "ഡ്രൈവേഴ്സ് ലിസ്റ്റിൽ ചേർത്തു! 🎉");
        setIsModalOpen(false);
      }
      
      fetchData(); 
    } catch (error) {
      toast.error("എന്തോ പ്രശ്നം സംഭവിച്ചു. വീണ്ടും ശ്രമിക്കുക!");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* 🌟 SEO - HELMET TAGS 🌟 */}
      <Helmet>
        <title>Drivers & Vehicles in Kalliasseri | Info Payangadi</title>
        <meta name="description" content="കല്യാശ്ശേരി നിയോജക മണ്ഡലത്തിലെ ഓട്ടോ, ടാക്സി, ഗുഡ്സ് തുടങ്ങിയ വാഹനങ്ങളുടെ വിവരങ്ങൾ ഒറ്റ ക്ലിക്കിൽ കണ്ടെത്താം." />
        <meta name="keywords" content="Payangadi Auto stand, Taxi Kalliasseri, Goods auto Cherukunnu, Ambulance Payangadi, Info Payangadi Directory" />
        <meta property="og:title" content="Drivers Directory - Info Payangadi" />
        <meta property="og:description" content="യാത്രകൾക്കും ആവശ്യങ്ങൾക്കും വാഹനങ്ങൾ എളുപ്പത്തിൽ വിളിക്കാം." />
        <meta property="og:image" content={`${window.location.origin}/og-image.jpg`} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 p-4 sm:p-6 pb-28 selection:bg-amber-200">
        
        {/* 🔥 HERO SECTION 🔥 */}
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between mb-8 mt-2 gap-4">
          
          <div className="flex items-center gap-3">
            <div className="p-3 sm:p-4 bg-amber-100 text-amber-600 rounded-full shadow-inner">
              <Car className="w-8 h-8 sm:w-9 sm:h-9" strokeWidth={2} />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900">
                Drivers <span className="text-amber-600">Directory</span>
              </h2>
              <p className="text-gray-500 text-xs sm:text-sm mt-0.5">യാത്രകൾക്കും ആവശ്യങ്ങൾക്കും വാഹനങ്ങൾ വിളിക്കാം</p>
            </div>
          </div>
          
          <div className="flex gap-2 w-full md:w-auto mt-2 md:mt-0">
            <button
              onClick={handleShareAll}
              className="flex-1 md:flex-none flex items-center justify-center gap-1.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 px-4 py-2.5 rounded-xl shadow-sm transition-all active:scale-95 font-bold text-sm"
            >
              <Share2 size={16} />
              <span>Share</span>
            </button>

            <button
              onClick={handleOpenRegister}
              className="flex-[2] md:flex-none flex items-center justify-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white px-5 py-2.5 rounded-xl shadow-md shadow-amber-500/20 transition-all active:scale-95 font-bold text-sm"
            >
              {currentUser ? (
                myProfile ? <><Edit3 size={16} /> Edit Profile</> : <><Plus size={16} /> Register Vehicle</>
              ) : (
                <><LogIn size={16} /> Login to Register</>
              )}
            </button>
          </div>

        </div>

        {/* 🔍 SEARCH & FILTER SECTION 🔥 */}
        <div className="max-w-3xl mx-auto mb-8 flex flex-col sm:flex-row gap-3">
          
          <div className="relative flex-1">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="പേരോ സ്ഥലമോ തിരയുക..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-11 pr-4 py-3 rounded-2xl border border-gray-200 shadow-sm focus:ring-2 focus:ring-amber-400 outline-none transition-all text-sm sm:text-base font-medium"
            />
          </div>

          <div className="relative w-full sm:w-48">
            <select
              value={vehicleFilter}
              onChange={(e) => setVehicleFilter(e.target.value)}
              className="w-full appearance-none py-3 pl-4 pr-10 rounded-2xl border border-gray-200 shadow-sm bg-white focus:ring-2 focus:ring-amber-400 outline-none transition-all text-gray-700 font-bold text-sm sm:text-base"
            >
              <option value="">എല്ലാ വാഹനങ്ങളും</option>
              <option value="Auto Rickshaw">Auto Rickshaw</option>
              <option value="Taxi Car">Taxi Car</option>
              <option value="Goods / Pickup">Goods Auto / Pickup</option>
              <option value="Tempo Traveller">Tempo Traveller</option>
              <option value="Bus / Tourist Bus">Bus / Tourist Bus</option>
              <option value="JCB / Crane">JCB / Crane</option>
              <option value="Ambulance">Ambulance</option>
              <option value="Other">Other</option>
            </select>
            <ChevronDown size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
          </div>

        </div>

        {/* 🚖 DRIVERS GRID 🔥 */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-6xl mx-auto">
          {sortedFiltered.map((d) => {
            const isMe = currentUser && d.userId === currentUser.uid;

            return (
              <motion.div
                key={d.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ y: -2 }}
                className={`bg-white p-4 sm:p-5 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden group flex flex-col justify-between ${
                  isMe ? "border border-amber-400 shadow-amber-100" : "border border-gray-100"
                }`}
              >
                {/* 🌟 YOUR PROFILE BADGE 🌟 */}
                {isMe && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-[9px] sm:text-[10px] font-black px-3 py-0.5 rounded-b-lg tracking-widest shadow-sm z-20">
                    YOUR PROFILE
                  </div>
                )}

                <div className="absolute -top-8 -right-8 w-20 h-20 bg-amber-50 rounded-full group-hover:scale-150 transition-transform duration-500 z-0"></div>

                <div className="flex items-start gap-3 sm:gap-4 mb-4 relative z-10 mt-1 sm:mt-0">
                  <div className="p-2.5 sm:p-3 rounded-xl sm:rounded-2xl bg-amber-50 text-amber-600 group-hover:bg-amber-500 group-hover:text-white transition-colors duration-300">
                    <Car size={22} className="sm:w-6 sm:h-6" />
                  </div>
                  <div>
                    <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-amber-500 block mb-0.5">
                      {d.vehicleType}
                    </span>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight">
                      {d.name}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-500 flex items-center gap-1 mt-1 font-medium">
                      <MapPin size={12} className="text-red-400" /> {d.place}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-gray-50 mt-auto relative z-10">
                  {/* EDIT / DELETE BUTTONS FOR OWNER */}
                  {isMe ? (
                    <div className="flex gap-1.5 w-full justify-between">
                      <div className="flex gap-1.5">
                        <button
                          onClick={handleOpenRegister}
                          className="p-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition"
                          title="Edit Profile"
                        >
                          <Edit3 size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(d.id)}
                          className="p-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition"
                          title="Delete Profile"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      <a 
                        href={`tel:${d.phone}`} 
                        className="flex items-center justify-center gap-1.5 bg-gray-100 text-gray-600 px-3 py-1.5 rounded-lg font-bold text-xs"
                      >
                        <PhoneCall size={14} /> My Number
                      </a>
                    </div>
                  ) : (
                    <>
                      <span className="text-xs sm:text-sm font-bold text-gray-400">
                        Verified Driver
                      </span>
                      <a 
                        href={`tel:${d.phone}`} 
                        className="flex items-center gap-1.5 bg-green-50 hover:bg-green-600 text-green-700 hover:text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-colors duration-300 font-bold text-xs sm:text-sm shadow-sm"
                      >
                        <PhoneCall size={14} className="sm:w-4 sm:h-4" /> Call Now
                      </a>
                    </>
                  )}
                </div>

              </motion.div>
            );
          })}

          {/* No Results Found */}
          {filtered.length === 0 && (
            <div className="col-span-full text-center bg-white p-8 rounded-2xl border border-gray-100">
              <Car size={40} className="mx-auto text-gray-300 mb-3" />
              <h3 className="text-lg font-bold text-gray-700">വാഹനങ്ങൾ കണ്ടെത്താനായില്ല</h3>
              <p className="text-sm text-gray-500 mt-1">നിങ്ങൾ തിരഞ്ഞ വിവരങ്ങൾ ഡാറ്റാബേസിൽ ഇല്ല.</p>
            </div>
          )}
        </div>

      </div>

      {/* 🌟 PREMIUM SLIDING MODAL 🌟 */}
      <AnimatePresence>
        {isModalOpen && currentUser && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4">
            
            <div className="absolute inset-0" onClick={() => setIsModalOpen(false)}></div>

            <motion.div
              initial={{ y: "100%", opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: "100%", opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="w-full max-w-lg bg-white rounded-t-[1.5rem] sm:rounded-[1.5rem] shadow-2xl relative z-10 max-h-[90vh] overflow-y-auto flex flex-col"
            >
              
              {/* Header */}
              <div className="sticky top-0 bg-white/90 backdrop-blur-md border-b border-gray-100 px-5 py-4 flex items-center justify-between rounded-t-[1.5rem] z-20">
                <h3 className="text-lg font-black text-gray-900 flex items-center gap-2">
                  <Car className="text-amber-600" size={20}/> {myProfile ? "Edit Your Vehicle" : "Register Vehicle"}
                </h3>
                <button onClick={() => setIsModalOpen(false)} className="p-1.5 bg-gray-100 text-gray-500 rounded-full hover:bg-red-100 hover:text-red-600 transition">
                  <X size={18} />
                </button>
              </div>

              {/* Form Body */}
              <div className="p-5 space-y-4 overflow-y-auto">
                <div>
                  <label className="text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Driver / Vehicle Name</label>
                  <input
                    value={form.name}
                    placeholder="ഡ്രൈവറുടെ പേര് അല്ലെങ്കിൽ വണ്ടിയുടെ പേര്"
                    className="w-full p-3 sm:p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-amber-400 transition text-sm sm:text-base"
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>

                <div>
                  <label className="text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Vehicle Type</label>
                  <div className="relative">
                    <select
                      value={form.vehicleType}
                      className="w-full p-3 sm:p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-amber-400 transition appearance-none text-sm sm:text-base"
                      onChange={(e) => setForm({ ...form, vehicleType: e.target.value })}
                    >
                      <option value="">വാഹനത്തിന്റെ തരം തിരഞ്ഞെടുക്കുക</option>
                      <option value="Auto Rickshaw">Auto Rickshaw (ഓട്ടോ)</option>
                      <option value="Taxi Car">Taxi Car (ടാക്സി കാർ)</option>
                      <option value="Goods / Pickup">Goods Auto / Pickup</option>
                      <option value="Tempo Traveller">Tempo Traveller</option>
                      <option value="Bus / Tourist Bus">Bus / Tourist Bus</option>
                      <option value="JCB / Crane">JCB / Crane / Earth Movers</option>
                      <option value="Ambulance">Ambulance (ആംബുലൻസ്)</option>
                      <option value="Other">Other (മറ്റുള്ളവ)</option>
                    </select>
                    <ChevronDown size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Mobile Number</label>
                    <input
                      type="tel"
                      value={form.phone}
                      placeholder="10 അക്ക നമ്പർ"
                      maxLength={10}
                      className="w-full p-3 sm:p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-amber-400 transition text-base tracking-wide font-bold text-gray-800"
                      onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "") })}
                    />
                  </div>

                  <div>
                    <label className="text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Stand / Place</label>
                    <input
                      value={form.place}
                      placeholder="സ്റ്റാൻഡ് / സ്ഥലം"
                      className="w-full p-3 sm:p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-amber-400 transition text-sm sm:text-base"
                      onChange={(e) => setForm({ ...form, place: e.target.value })}
                    />
                  </div>
                </div>

              </div>

              {/* Footer Button */}
              <div className="p-5 pb-24 sm:pb-5 border-t border-gray-100 bg-gray-50 sm:rounded-b-[1.5rem]">
                <button
                  onClick={handlePreSubmit}
                  disabled={loading}
                  className="w-full bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white p-3.5 rounded-xl font-bold tracking-wide shadow-md shadow-amber-200 transition-all active:scale-95 text-sm sm:text-base"
                >
                  {loading ? "Please wait..." : (myProfile ? "Update Profile" : "Save Details")}
                </button>
              </div>

            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* 🔥 UNIFIED CONFIRMATION MODAL 🔥 */}
      <ConfirmModal
        isOpen={confirmConfig.isOpen}
        message={confirmConfig.message}
        onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
        onConfirm={executeConfirmAction}
      />

    </>
  );
}